// © Microsoft Corporation. All rights reserved.

import { Size1ColumnWidth, Size2ColumnWidth, Size3ColumnWidth, Size4ColumnWidth, Size5ColumnWidth } from "./ColumnWidths";

import { PageUtility } from "@msnews/core";
import { findIndex } from "lodash-es";
import { toPx } from "@microsoft/fast-jss-utilities";

/**
 * Describes possible arrangements of columns. For example c3 represents 3 columns.
 *
 * Each arrangement is valid for a particular range of window widths. For example, for window widths from 996px to 1319px,
 * we will use the c3 arrangement, or 3 columns.
 *
 * Currently each arrangement describes a column count, but in the future, there may be separate 3-column "large"
 * and 3-column "small" arrangements, like on msn.com.
 */
export enum ColumnArrangement {
    c1 = "c1",
    c2 = "c2",
    c3 = "c3",
    c4 = "c4",
    c5 = "c5"
}

let minViewportWidthOverrides: Map<ColumnArrangement, number>;

/**
 * Override the min viewport widths for the given column arrangements.
 * @param overrides The overrides to use for the column arrangements.
 */
export function overrideMinViewportWidths(overrides: Map<ColumnArrangement, number>): void {
    minViewportWidthOverrides = overrides;
}

/**
 * Returns the column arrangement
 * @param breakpoint The breakpoint we want to map to column arrangement
 */
export function mapBreakpointToColumnArrangement(breakpoint: number): ColumnArrangement {
    switch (breakpoint) {
        case 0:
            return ColumnArrangement.c1;
        case 1:
            return ColumnArrangement.c2;
        case 2:
            return ColumnArrangement.c3;
        case 3:
            return ColumnArrangement.c4;
        case 4:
            return PageUtility.fiveColSupported() ? ColumnArrangement.c5 : null;
        default:
            return null;
    }
}

/**
 * Update ColumnArrangements and columnArrangementMap in case five column layout is supported
 */
export function updateColumnArrangementsForFiveCol(): void {
    if (PageUtility.fiveColSupported() && columnArrangements.length === 4) {
        const colFiveArrangementDefinition = new ColumnArrangementDefinition(ColumnArrangement.c5, Size5ColumnWidth);
        columnArrangements.push(colFiveArrangementDefinition);
        columnArrangementMap[ColumnArrangement.c5] = colFiveArrangementDefinition;
    }
}

/**
 * Defines the properties of a particular column arrangement.
 */
export class ColumnArrangementDefinition {
    /**
     * Creates a column arrangement definition.
     * @param arrangement The column arrangement being defined.
     * @param contentSizePx The size of the content in pixels for this arrangement.
     * @param isFirstBreakpoint True if this is the first breakpoint.
     */
    constructor(public arrangement: ColumnArrangement, public contentSizePx: number, private isFirstBreakpoint?: boolean) { }

    /**
     * Gets the minimum required window width to trigger this column arrangement.
     */
    public get minViewportWidthPx(): number {
        if (this.isFirstBreakpoint) {
            return 0;
        }

        if (minViewportWidthOverrides) {
            const widthOverride = minViewportWidthOverrides.get(this.arrangement);
            if (widthOverride) {
                return widthOverride;
            }
        }

        return this.contentSizePx + contentBufferPx;
    }
}

/**
 * The list of possible column arrangement definitions. Use this to determine what window sizes are
 * appropriate for each column arrangement.
 */
export const columnArrangements: ColumnArrangementDefinition[] = [
    new ColumnArrangementDefinition(ColumnArrangement.c1, Size1ColumnWidth, true),
    new ColumnArrangementDefinition(ColumnArrangement.c2, Size2ColumnWidth),
    new ColumnArrangementDefinition(ColumnArrangement.c3, Size3ColumnWidth),
    new ColumnArrangementDefinition(ColumnArrangement.c4, Size4ColumnWidth)];

// Add five column arrangement when supported.
// Note this will not currently work with SSR as no reset functionality is provided and this edits a global constant.
if (PageUtility.fiveColSupported()) {
    columnArrangements.push(new ColumnArrangementDefinition(ColumnArrangement.c5, Size5ColumnWidth));
}

/**
 * A map of column arrangements to definitions.
 */
const columnArrangementMap: { [columnArrangement in ColumnArrangement]?: ColumnArrangementDefinition } = {};
for (const columnArrangementDefinition of columnArrangements) {
    columnArrangementMap[columnArrangementDefinition.arrangement] = columnArrangementDefinition;
}

/**
 * The number of pixels to add on top of the content width in order to get the window width required to show it.
 * This buffer guarantees space between the content and the edge of the window, and also space for the scrollbar,
 * which is also counted when determining breakpoints.
 */
const contentBufferPx = 32;

/**
 * Get list of breakpoints from column arrangements
 */
function getBreakpointsFromColumnArrangements(): number[] {
    const breakpoints: number[] = [0];

    for (let i = 1; i < columnArrangements.length; i++) {
        breakpoints.push(columnArrangements[i].minViewportWidthPx);
    }

    return breakpoints;
}

/**
 * Breakpoints that are calculated from the column arrangements
 */
export const columnBreakpoints = getBreakpointsFromColumnArrangements();

/**
 * Gets the content width for the given column arrangement.
 * @param columnArrangement The column arrangement to look up.
 * @returns The width in pixels of the content.
 */
export function contentWidthPxForColumnArrangement(columnArrangement: ColumnArrangement): number {
    return columnArrangementMap[columnArrangement].contentSizePx;
}

/**
 * Gets the minimum window width for the given column arrangement.
 * @param columnArrangement The column arrangement to look up.
 * @returns The minimum width for the window to show this column arrangement.
 */
export function minWindowWidthPxForColumnArrangement(columnArrangement: ColumnArrangement): number {
    return columnArrangementMap[columnArrangement].minViewportWidthPx;
}

/**
 * Gets the maximum window width for the given column arrangement.
 * @param columnArrangement The column arrangement to look up.
 * @returns The maximum width for the window to show this column arrangement. NaN if there is no maximum.
 */
export function maxWindowWidthPxForColumnArrangement(columnArrangement: ColumnArrangement): number {
    const arrangementIndex = findIndex(columnArrangements, a => a.arrangement === columnArrangement);

    if (arrangementIndex < columnArrangements.length - 1) {
        return columnArrangements[arrangementIndex + 1].minViewportWidthPx - 1;
    }

    return NaN;
}

/**
 * Helper to efficiently get the media query string to be used for given min/max column arrangements
 * It can be used in conjunction with `window.matchMedia` to target specific page widths, and it is also
 * used internally to generate the equivalent css selectors in `getMediaQuerySelector`
 * @param minArrangement The min column arrangement to use for the @media query (inclusive)
 * @param maxArrangement The max column arrangement to use for the @media query (inclusive)
 * @returns the @media query for the specified min/max.
 */
export function getMediaQuery(minArrangement: ColumnArrangement | null, maxArrangement: ColumnArrangement | null): string {
    const queries = [];

    if (minArrangement) {
        const minArrangementDefinition = columnArrangementMap[minArrangement];
        const minWidthPx = minArrangementDefinition.minViewportWidthPx;

        if (minWidthPx > 0) {
            queries.push(`(min-width: ${toPx(minWidthPx)})`);
        }
    }

    if (maxArrangement) {
        const maxArrangementDefinition = columnArrangementMap[maxArrangement];
        const maxArrangementIndex = columnArrangements.indexOf(maxArrangementDefinition);

        if (maxArrangementIndex < columnArrangements.length - 1) {
            queries.push(`(max-width: ${toPx(columnArrangements[maxArrangementIndex + 1].minViewportWidthPx - 1)})`);
        }
    }

    return queries.join(" and ");
}

/**
 * Helper to efficiently get the media query string to be used for the given column arrangement
 * It can be used in conjunction with `window.matchMedia` to target specific page widths, and it is also
 * used internally to generate the equivalent css selectors in `getMediaQuerySelector`
 * @param columnArrangement The column arrangement to target
 * @returns The media query to target that column arrangement
 */
export function getMediaQueryFor(columnArrangement: ColumnArrangement): string {
    return getMediaQuery(columnArrangement, columnArrangement);
}

/**
 * Helper to efficiently get the CSS selector to be used for apply styles for given min/max arrangements
 * @param minArrangement The min column arrangement to use for the media query (inclusive)
 * @param maxArrangement The max column arrangement to use for the media query (inclusive)
 * @returns the css media query selector for the specified min/max.
 */
export function getMediaQuerySelector(minArrangement: ColumnArrangement | null, maxArrangement: ColumnArrangement | null): string {
    const mediaQuery = getMediaQuery(minArrangement, maxArrangement);
    return mediaQuery ? "@media " + mediaQuery : "";
}

/**
 * Helper to efficiently get the CSS selector to be used for apply styles for the given column arrangement.
 * @param columnArrangement The column arrangement to target
 * @returns the css media query selector for the specified column arrangement.
 */
export function getMediaQuerySelectorFor(columnArrangement: ColumnArrangement): string {
    return getMediaQuerySelector(columnArrangement, columnArrangement);
}

/**
 * Callback type for subscribing to BreakpointManager breakpoint changed events.
 */
export type BreakpointManagerCallback = (columnArrangement: ColumnArrangement) => void;

/**
 * Breakpoint manager common interface.
 */
export interface BreakpointManager {
    /**
     * Gets the current column arrangement.
     */
    currentColumnArrangement: ColumnArrangement;

    /**
     * Subscribes for changes
     * @param callback The method to call when the column arrangement changes.
     */
    subscribe(callback: BreakpointManagerCallback): void;

    /**
     * Unsubscribe from changes
     * @param callback The callback to unregister.
     */
    unsubscribe(callback: BreakpointManagerCallback): void;
}